import React, { Component } from 'react'
import { PropTypes } from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GoogleMap from 'google-map-react'
import get from 'lodash.get'
import { fitBounds } from 'google-map-react/utils'

import MainMarker from './MainMarker'
import MapMarker from './MapMarker'
import * as actions from '../../actions'
import * as utils from '../../utils'

/*
 Google Maps component
*/
class Map extends Component {
  static propTypes = {
    properties: PropTypes.object.isRequired,
    onMarkerClick: PropTypes.func,
  }

  constructor(props) {
    const defZoom = parseInt(process.env.REACT_APP_DEFAULT_ZOOM, 10)
    super(props)
    this.state = {
      hoveredMarkerId: null,
      zoom: this.props.initialZoom ? this.props.initialZoom : defZoom,
      center: this.props.center,
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selected !== this.props.selected) {
      this.setState({
        hoveredMarkerId: nextProps.selected,
      })
    }
    if (nextProps.viewPort && nextProps.viewPort !== this.props.viewPort) {
      this.setCenterZoom(nextProps.viewPort)
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.statusUpdate !== prevProps.statusUpdate) {
      this.fetchPropertiesData(this.state.bounds)
    }
  }

  onChange({ center, zoom, bounds }) {
    const { actions } = this.props
    actions.changeMap(zoom, bounds)
    if (center.lat !== parseFloat(process.env.REACT_APP_DEFAULT_LAT) && center.lng !== parseFloat(process.env.REACT_APP_DEFAULT_LNG)) {
      this.fetchPropertiesData(bounds)
      actions.mapCenterChange(center)
    }
    this.setState({
      center,
      zoom,
      bounds,
    })
    this.props.mapBoundsUpdate && this.props.mapBoundsUpdate(this.state.bounds)
  }

  fetchPropertiesData(bounds) {
    const { actions, filters, queryType } = this.props
    const isFranchisee = queryType === 'Franchisee'
    const offset = 0
    const cachePropertyMarkers = true

    const geoRect = {
      w: get(bounds, 'sw.lng', parseFloat(process.env.REACT_APP_DEFAULT_LNG)),
      e: get(bounds, 'ne.lng', parseFloat(process.env.REACT_APP_DEFAULT_LNG)),
      n: get(bounds, 'ne.lat', parseFloat(process.env.REACT_APP_DEFAULT_LAT)),
      s: get(bounds, 'sw.lat', parseFloat(process.env.REACT_APP_DEFAULT_LAT)),
    }

    // bounded box query
    const query = utils.getBoundedQuery(
      geoRect,
      {
        priceMin: filters.priceMinimum,
        priceMax: filters.priceMaximum,
        beds: filters.numberOfBedrooms,
        baths: filters.numberOfBathrooms,
        areaMin: filters.areaMinimum,
        areaMax: filters.areaMaximum,
        homeTypes: filters.homeTypesSelected,
        types: filters.propertyTypesSelected,
      },
      this.props.queryType,
      get(this.props, 'id', 0),
      offset
    )
    actions.fetchProperties({ query, isFranchisee, cachePropertyMarkers })
  }

  setHoveredMarkerId(id = null) {
    const { actions } = this.props
    actions.mapMarkerHover(id)
    this.setState({
      hoveredMarkerId: id,
    })
  }

  setCenterZoom(viewPort) {
    const size = {
      height: window.innerHeight,
      width: window.innerWidth,
    }

    const bounds = {
      ne: {
        lat: viewPort.get('northeast', 'lat'),
        lng: viewPort.get('northeast', 'lng'),
      },
      sw: {
        lat: viewPort.get('southwest', 'lat'),
        lng: viewPort.get('southwest', 'lng'),
      },
    }

    this.setState(fitBounds(bounds, size))
  }

  render() {
    const { center, mobile, pageType, cachedPropertyMarkers, properties } = this.props
    const { zoom } = this.state
    const { hoveredMarkerId } = this.state
    const linkToBase = pageType === 'home' ? 'properties' : 'details'
    let zoomMap = isNaN(zoom) ? process.env.REACT_APP_DEFAULT_ZOOM : zoom
    const showPriceTag = zoomMap > process.env.REACT_APP_DEFAULT_ZOOM - 1
    const searchString = pageType === 'home' ? this.props.searchString : ''
    const createMapOptions = maps => {
      return {
        zoomControl: !mobile,
        minZoom: 7,
        maxZoom: 19,
        fullscreenControl: !mobile,
        fullscreen: !!mobile,
        gestureHandling: 'greedy',
        styles: [
          {
            stylers: [{ visibility: 'on' }, { saturation: -100 }, { gamma: 0.8 }, { lightness: 4 }],
          },
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
      }
    }
    const filteredProps = utils.filteredProperties(properties.valueSeq())
    return (
      <GoogleMap
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_TOKEN }}
        options={createMapOptions}
        hoverDistance={30}
        center={center}
        zoom={zoomMap}
        onChange={(center, zoom, bounds, marginBounds) => this.onChange(center, zoom, bounds, marginBounds)}
        onChildMouseEnter={id => this.setHoveredMarkerId(parseInt(id, 10))}
        onChildMouseLeave={() => this.setHoveredMarkerId(null)}>
        <MainMarker lat={center.lat} lng={center.lng} />
        {filteredProps.map((p, ix) => (
          <MapMarker
            key={p.id}
            property={p}
            linkTo={`/${linkToBase}/${p.id}?${searchString}`}
            onMarkerClick={() => this.props.onMarkerClick()}
            lat={p.lat}
            lng={p.lng}
            hovered={hoveredMarkerId === parseInt(p.id, 10)}
            showPriceTag={showPriceTag}
          />
        ))}
        {cachedPropertyMarkers.valueSeq().map((p, ix) => (
          <MapMarker
            key={p.id}
            property={p}
            linkTo={`/${linkToBase}/${p.id}?${searchString}`}
            onMarkerClick={() => this.props.onMarkerClick()}
            lat={p.lat}
            lng={p.lng}
            hovered={hoveredMarkerId === parseInt(p.id, 10)}
            showPriceTag={showPriceTag}
          />
        ))}
      </GoogleMap>
    )
  }
}

Map.defaultProps = {
  clusterRadius: 60,
  hoverDistance: 30,
  options: {
    minZoom: 7,
    maxZoom: 15,
  },
  style: {
    position: 'relative',
    margin: 0,
    padding: 0,
    flex: 1,
  },
  center: {
    lat: parseFloat(process.env.REACT_APP_DEFAULT_LAT),
    lng: parseFloat(process.env.REACT_APP_DEFAULT_LNG),
  },
  zoom: 9,
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch),
  }
}
function mapStateToProps(state) {
  return {
    properties: state.properties.get('data'),
    cachedPropertyMarkers: state.cachedPropertyMarkers.get('data'),
    selected: state.properties.get('selected'),
    filters: state.filters,
    queryType: state.subdomain.data.record_type,
    mapZoom: state.map.get('zoom'),
    statusUpdate: state.user.statusUpdate,
  }
}

const map = connect(
  mapStateToProps,
  mapDispatchToProps
)(Map)

export default map



// WEBPACK FOOTER //
// ./src/components/map/Map.js